import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdEmail, MdNotificationsActive, MdRestartAlt, MdSave } from 'react-icons/md';
import {
  AppCard,
  AppInput,
  AppTextarea,
  OutlineButton,
  PageLoader,
  PrimaryButton,
  StatusAlertCard,
} from '../../../components/ui';
import {
  isConfigFieldVisible,
  SYSTEM_CONFIG_DEFS,
  validateConfigValue,
} from '../../../data/system/systemConfig';
import {
  getSystemConfig,
  resetSystemConfig,
  updateSystemConfig,
} from '../../../repositories/systemConfig.repository';
import {
  enableAndRegisterWebPush,
  getPushStatus,
  sendTestNotification,
} from '../../../repositories/deviceTokens.repository';
import { getEmailStatus, sendTestEmail } from '../../../repositories/email.repository';
import { logCurrentStaffActivity } from '../../../utils/activityLogHelper';
import { useStaffAccess } from '../../../utils/staffAccess';
import { useHubHeaderActions } from '../hub/hubUi';

function toDraft(config) {
  return SYSTEM_CONFIG_DEFS.reduce((acc, def) => {
    if (def.type === 'boolean') acc[def.key] = Boolean(config[def.key]);
    else acc[def.key] = String(config[def.key] ?? '');
    return acc;
  }, {});
}

const GROUP_ORDER = ['Vendors', 'Indents', 'General'];

/** Browser push enable/test UI — local/dev only (hidden in production builds). */
const SHOW_BROWSER_NOTIFICATION_DEBUG =
  process.env.NODE_ENV !== 'production';

export default function SystemConfigPage({ embedded = false } = {}) {
  const toast = useToast();
  const { canEditSystemConfig } = useStaffAccess();
  const canEdit = canEditSystemConfig();
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushStatus, setPushStatus] = useState({
    configured: false,
    permission: 'default',
    hasLocalToken: false,
  });
  const [pushBusy, setPushBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [testDelaySeconds, setTestDelaySeconds] = useState('0');
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailSubject, setEmailSubject] = useState('Utkarsh test email');
  const [emailBody, setEmailBody] = useState(
    'This is a test message from Utkarsh.',
  );
  const [emailBusy, setEmailBusy] = useState(false);
  const emailCompose = useDisclosure();
  const [emailStatus, setEmailStatus] = useState({
    configured: false,
    sendEnabled: false,
  });

  const refreshPushStatus = async () => {
    try {
      const status = await getPushStatus();
      setPushStatus(status);
    } catch {
      setPushStatus({
        configured: false,
        permission: 'unsupported',
        hasLocalToken: false,
      });
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await getSystemConfig();
        if (!cancelled) setDraft(toDraft(config));
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load configuration',
            status: 'error',
            position: 'top-right',
          });
          setDraft(toDraft({}));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  useEffect(() => {
    if (!SHOW_BROWSER_NOTIFICATION_DEBUG) return undefined;
    refreshPushStatus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await getEmailStatus();
        if (!cancelled) setEmailStatus(status);
      } catch {
        if (!cancelled) {
          setEmailStatus({ configured: false, sendEnabled: false });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    SYSTEM_CONFIG_DEFS.forEach((def) => {
      if (!map.has(def.group)) map.set(def.group, []);
      map.get(def.group).push(def);
    });
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => [g, map.get(g)]);
  }, []);

  const patch = (key, value) => {
    setDraft((d) => {
      const next = { ...d, [key]: value };
      if (key === 'vendorSelfRegistrationEnabled' && value) {
        next.vendorSelfRegistrationDisabledReason = '';
      }
      return next;
    });
    setErrors((e) => {
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const handleSave = async () => {
    const nextErrors = {};
    const patchValues = {};
    SYSTEM_CONFIG_DEFS.forEach((def) => {
      const result = validateConfigValue(def.key, draft[def.key], draft);
      if (!result.ok) nextErrors[def.key] = result.error;
      else patchValues[def.key] = result.value;
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast({
        title: 'Fix the highlighted settings',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }

    setSaving(true);
    try {
      const saved = await updateSystemConfig(patchValues);
      setDraft(toDraft(saved));
      logCurrentStaffActivity({
        actionType: 'UPDATE',
        module: 'Master Data',
        summary: 'Updated system configuration',
        entityType: 'system_config',
        entityId: 'SYSTEM',
      });
      toast({
        title: 'System configuration saved',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to save',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const reset = await resetSystemConfig();
      setDraft(toDraft(reset));
      setErrors({});
      toast({
        title: 'Reset to defaults',
        status: 'info',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to reset',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const openEmailCompose = () => {
    if (!emailSubject.trim()) setEmailSubject('Utkarsh test email');
    if (!emailBody.trim()) {
      setEmailBody('This is a test message from Utkarsh.');
    }
    emailCompose.onOpen();
  };

  const handleSendTestEmail = async () => {
    const to = emailTo.trim();
    if (!to) {
      toast({
        title: 'Enter a To address',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    setEmailBusy(true);
    try {
      await sendTestEmail({
        to,
        cc: emailCc.trim() || null,
        subject: emailSubject.trim() || 'Utkarsh test email',
        body: emailBody.trim() || null,
      });
      emailCompose.onClose();
      toast({
        title: `Test email sent to ${to}`,
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to send test email',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setEmailBusy(false);
    }
  };

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      await enableAndRegisterWebPush();
      await refreshPushStatus();
      toast({
        title: 'Browser notifications enabled',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Could not enable notifications',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setPushBusy(false);
    }
  };

  const handleTestNotification = async () => {
    setTestBusy(true);
    try {
      if (!pushStatus.hasLocalToken) {
        await enableAndRegisterWebPush();
        await refreshPushStatus();
      }

      // Immediate local tray ping — proves OS/Chrome permission works even if FCM is broken.
      if (Notification.permission === 'granted' && navigator.serviceWorker) {
        try {
          const reg = await navigator.serviceWorker.ready;
          await reg.showNotification('Utkarsh local test', {
            body: 'If you see this, browser notifications work. Waiting for FCM…',
            icon: '/favicon.svg',
          });
        } catch {
          // ignore
        }
      }

      const delaySec = Math.min(
        60,
        Math.max(0, Number.parseInt(testDelaySeconds, 10) || 0)
      );
      if (delaySec > 0) {
        toast({
          title: `Sending test in ${delaySec}s…`,
          status: 'info',
          position: 'top-right',
          duration: Math.min(delaySec * 1000, 5000),
        });
        await new Promise((resolve) => setTimeout(resolve, delaySec * 1000));
      }
      const data = await sendTestNotification({
        title: 'Test notification',
        body:
          delaySec > 0
            ? `Test push (delayed ${delaySec}s) from System configuration.`
            : 'This is a test push from System configuration.',
      });
      const fcm = data?.fcm;
      const tokenCount = Number(fcm?.tokenCount ?? 0);
      const successCount = Number(fcm?.successCount ?? 0);
      const errors = Array.isArray(fcm?.errors) ? fcm.errors : [];
      if (tokenCount === 0) {
        toast({
          title: 'No device token on server',
          description: 'Click Enable browser notifications again, then retry.',
          status: 'warning',
          position: 'top-right',
        });
      } else if (successCount <= 0) {
        toast({
          title: 'FCM delivery failed',
          description:
            errors[0] ||
            'Token may be invalid. Unregister SW, Enable notifications again.',
          status: 'error',
          position: 'top-right',
          duration: 9000,
        });
      } else {
        toast({
          title: 'FCM accepted by Firebase',
          description: `success=${successCount}. If nothing appears: minimize Chrome, check macOS Notifications for Google Chrome, and watch DevTools → Console for [FCM SW].`,
          status: 'success',
          position: 'top-right',
          duration: 9000,
        });
      }    } catch (err) {
      toast({
        title: err?.message || 'Test notification failed',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setTestBusy(false);
    }
  };

  const renderField = (def, isLast) => {
    if (!draft || !isConfigFieldVisible(def, draft)) return null;

    const hint =
      def.type === 'number'
        ? `${def.description} (${def.min}–${def.max})`
        : def.description;

    if (def.type === 'text') {
      return (
        <Box
          key={def.key}
          pt={2.5}
          pb={isLast ? 0 : 2.5}
          borderBottom={isLast ? 'none' : '1px solid'}
          borderColor="border.subtle"
        >
          <AppTextarea
            label={def.label}
            isRequired={Boolean(def.requiredWhen)}
            value={draft[def.key]}
            isReadOnly={!canEdit}
            onChange={(e) => patch(def.key, e.target.value)}
            rows={2}
            fontSize="14px"
            maxLength={def.maxLength}
            isInvalid={!!errors[def.key]}
            errorMessage={errors[def.key]}
            helperText={hint}
          />
        </Box>
      );
    }

    return (
      <Flex
        key={def.key}
        py={2.5}
        align="center"
        justify="space-between"
        gap={4}
        borderBottom={isLast ? 'none' : '1px solid'}
        borderColor="border.subtle"
      >
        <Box minW={0} flex="1">
          <Text fontWeight="600" fontSize="13px" lineHeight="1.3">
            {def.label}
          </Text>
          <Text fontSize="12px" color="text.secondary" mt={0.5} lineHeight="1.35">
            {hint}
          </Text>
          {errors[def.key] ? (
            <Text fontSize="12px" color="red.500" mt={1}>
              {errors[def.key]}
            </Text>
          ) : null}
        </Box>
        {def.type === 'boolean' ? (
          <Switch
            colorScheme="yellow"
            size="md"
            flexShrink={0}
            isChecked={Boolean(draft[def.key])}
            isDisabled={!canEdit || saving}
            onChange={(e) => patch(def.key, e.target.checked)}
          />
        ) : (
          <Input
            value={draft[def.key]}
            isReadOnly={!canEdit}
            inputMode="numeric"
            onChange={(e) =>
              patch(def.key, e.target.value.replace(/\D/g, '').slice(0, 4))
            }
            h="36px"
            w="72px"
            minW="72px"
            px={2}
            textAlign="center"
            fontSize="14px"
            fontWeight="600"
            bg="bg.input"
            borderColor={errors[def.key] ? 'red.400' : 'border.subtle'}
            flexShrink={0}
            isDisabled={saving}
          />
        )}
      </Flex>
    );
  };

  const pushStatusLabel = !pushStatus.configured
    ? 'Firebase web config not set up yet'
    : pushStatus.permission === 'granted' && pushStatus.hasLocalToken
      ? 'Browser push is enabled on this device'
      : pushStatus.permission === 'denied'
        ? 'Notifications blocked in the browser'
        : 'Browser push not enabled on this device yet';

  const saveRef = useRef(handleSave);
  const resetRef = useRef(handleReset);
  saveRef.current = handleSave;
  resetRef.current = handleReset;

  useHubHeaderActions(
    () =>
      embedded && canEdit && draft ? (
        <>
          <OutlineButton
            size="sm"
            leftIcon={<MdRestartAlt />}
            onClick={() => resetRef.current()}
            isLoading={saving}
          >
            Reset defaults
          </OutlineButton>
          <PrimaryButton
            size="sm"
            leftIcon={<MdSave />}
            onClick={() => saveRef.current()}
            isLoading={saving}
          >
            Save changes
          </PrimaryButton>
        </>
      ) : null,
    [embedded, canEdit, saving, draft],
  );

  if (loading || !draft) {
    return <PageLoader label="Loading system configuration…" />;
  }

  const actionButtons = canEdit ? (
    <Flex gap={2} wrap="wrap">
      <OutlineButton
        size="sm"
        leftIcon={<MdRestartAlt />}
        onClick={handleReset}
        isLoading={saving}
      >
        Reset defaults
      </OutlineButton>
      <PrimaryButton
        size="sm"
        leftIcon={<MdSave />}
        onClick={handleSave}
        isLoading={saving}
      >
        Save changes
      </PrimaryButton>
    </Flex>
  ) : null;

  return (
    <Box>
      {!embedded ? (
        <Flex
          justify="space-between"
          align={{ base: 'start', md: 'center' }}
          gap={4}
          mb={4}
          wrap="wrap"
        >
          <Box>
            <Heading size={{ base: 'lg', xl: 'xl' }}>System configuration</Heading>
            <Text color="text.secondary" mt={2} maxW="720px">
              Business rules and feature toggles used across the app — change them here
              instead of in code.
            </Text>
          </Box>
          {actionButtons}
        </Flex>
      ) : null}

      {!canEdit ? (
        <Box mb={3}>
          <StatusAlertCard
            title="Read only"
            body="You can view these settings but need the System configuration edit right to change them."
          />
        </Box>
      ) : null}

      <VStack align="stretch" spacing={3}>
        {groups.map(([group, defs]) => {
          const visible = defs.filter((d) => isConfigFieldVisible(d, draft));
          return (
            <AppCard key={group} px={4} py={3}>
              <Text
                fontSize="12px"
                fontWeight="700"
                letterSpacing="0.04em"
                textTransform="uppercase"
                color="text.secondary"
                mb={1}
              >
                {group}
              </Text>
              {visible.map((def, i) => renderField(def, i === visible.length - 1))}
            </AppCard>
          );
        })}

        <AppCard px={4} py={3}>
          <Text
            fontSize="12px"
            fontWeight="700"
            letterSpacing="0.04em"
            textTransform="uppercase"
            color="text.secondary"
            mb={1}
          >
            Email
          </Text>
          <Flex py={2.5} align="center" justify="space-between" gap={4}>
            <Box minW={0} flex="1">
              <Text fontWeight="600" fontSize="13px" lineHeight="1.3">
                Test message
              </Text>
              <Text
                fontSize="12px"
                color="text.secondary"
                mt={0.5}
                lineHeight="1.35"
              >
                {!emailStatus.configured
                  ? 'Email is not set up on the API yet.'
                  : emailStatus.sendEnabled
                    ? 'Send a test message from the Utkarsh mailbox.'
                    : 'Email is set up, sending is currently off.'}
              </Text>
            </Box>
            <OutlineButton
              size="sm"
              leftIcon={<MdEmail size={16} />}
              onClick={openEmailCompose}
              isDisabled={!canEdit}
              flexShrink={0}
            >
              Compose
            </OutlineButton>
          </Flex>
        </AppCard>

        {SHOW_BROWSER_NOTIFICATION_DEBUG ? (
          <AppCard px={4} py={3}>
            <Text
              fontSize="12px"
              fontWeight="700"
              letterSpacing="0.04em"
              textTransform="uppercase"
              color="text.secondary"
              mb={1}
            >
              Notifications
            </Text>
            <Text fontSize="12px" color="text.secondary" mb={2} lineHeight="1.4">
              Debug only — not shown in production. Enable browser push on this
              device, then send a test.
            </Text>
            <Text fontSize="13px" fontWeight="600" mb={2.5}>
              {pushStatusLabel}
            </Text>
            <Flex gap={2} wrap="wrap" align="center">
              <Input
                value={testDelaySeconds}
                inputMode="numeric"
                onChange={(e) =>
                  setTestDelaySeconds(
                    e.target.value.replace(/\D/g, '').slice(0, 2)
                  )
                }
                h="40px"
                w="72px"
                minW="72px"
                px={2}
                textAlign="center"
                fontSize="14px"
                fontWeight="600"
                bg="bg.input"
                aria-label="Send after seconds"
              />
              <Text fontSize="12px" color="text.secondary" mr={1}>
                sec delay
              </Text>
              <OutlineButton size="sm" onClick={handleEnablePush} isLoading={pushBusy}>
                Enable notifications
              </OutlineButton>
              <PrimaryButton
                size="sm"
                leftIcon={<MdNotificationsActive size={16} />}
                onClick={handleTestNotification}
                isLoading={testBusy}
              >
                Send test
              </PrimaryButton>
            </Flex>
            {!pushStatus.configured ? (
              <Text fontSize="12px" color="text.secondary" mt={2} lineHeight="1.4">
                Copy <code>public/firebase-config.example.json</code> to{' '}
                <code>public/firebase-config.json</code> and fill values from
                Firebase Console (Web app + Web Push VAPID key).
              </Text>
            ) : null}
          </AppCard>
        ) : null}
      </VStack>

      <Modal
        isOpen={emailCompose.isOpen}
        onClose={() => {
          if (!emailBusy) emailCompose.onClose();
        }}
        size="xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Compose test email</ModalHeader>
          {!emailBusy ? <ModalCloseButton /> : null}
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <AppInput
                label="To"
                type="email"
                isRequired
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="recipient@example.com"
                helperText="Separate multiple addresses with a comma"
              />
              <AppInput
                label="CC"
                type="email"
                value={emailCc}
                onChange={(e) => setEmailCc(e.target.value)}
                placeholder="Optional"
              />
              <AppInput
                label="Subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Utkarsh test email"
              />
              <AppTextarea
                label="Body"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={8}
                placeholder="Message"
              />
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <OutlineButton onClick={emailCompose.onClose} isDisabled={emailBusy}>
              Cancel
            </OutlineButton>
            <PrimaryButton
              leftIcon={<MdEmail size={16} />}
              onClick={handleSendTestEmail}
              isLoading={emailBusy}
            >
              Send
            </PrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
